import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import { UnsavedChanges } from '@deltek/specification-client/core/Constants/messages'
import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    makeStyles,
    Tab,
    Tabs,
    Theme,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import React, {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import { ListingDetail } from '@deltek/specification-client/core/Models/listing'
import { ManufacturerProductPropertyDetail } from '@deltek/specification-client/core/Models/product'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import colors from '../../../listingColors'
import {
    ProductContext,
    ProductContextType,
    PropertyType,
    SupportedOptionsObject,
} from '../../ListingDetailsPage'
import PremiumInfoIconTooltip from '../../PremiumInfoIconTooltip'
import PropertiesContainer from '../PropertiesContainer'
import { isMasterSpecPropertyGroup } from '../propertiesUtils'
import {
    MANUFACTURER_OPTION_INFO_DIALOG_TEXT,
    MANUFACTURER_OPTIONS,
    MASTERSPEC_OPTIONS,
} from './constants'
import NonMSContainer from './NonMasterSpec/NonMSContainer'
import {
    NonMSPropertyType,
    NonMSSubPropertyType,
} from './NonMasterSpec/nonMSTypes'
import NonMSContainerViewMode from './NonMasterSpec/viewNonMS/NonMSContainerViewMode'
import { PPL_ACTIONS, usePPLContext } from './PPLProvider'
import { composePropertiesInput, composeSelectedOptions } from './pplUtils'

const {
    buttonPrimary,
    buttonSecondary,
    defaultLight,
    propertiesDivider,
    productDetailsText,
    titleColor,
} = colors

export const useStyles = makeStyles((theme: Theme) => ({
    actionContainer: {
        height: pxToRem(70),
        borderTop: `1px solid ${propertiesDivider}`,
        paddingRight: theme.spacing(4),
        marginTop: theme.spacing(4),
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${propertiesDivider}`,
        '& > span': {
            fontWeight: 400,
            fontSize: pxToRem(24),
            lineHeight: pxToRem(28),
            color: productDetailsText,
        },
    },
    dialogContent: {
        minHeight: 475,
        paddingRight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    dialogContainer: {
        overflowX: 'hidden',
    },
    propertiesContainer: {
        overflowY: 'auto',
        padding: theme.spacing(4),
        minHeight: pxToRem(400),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(2),
        color: theme.palette.grey[500],
    },
    tabs: {
        fontWeight: 500,
        fontSize: pxToRem(18),
        marginBottom: '4px',
        height: 'fit-content',
        padding: '0 12px',
        '&.Mui-selected': {
            color: buttonPrimary,
            borderBottom: `4px solid ${buttonPrimary}`,
            marginBottom: 0,
            paddingTop: theme.spacing(0.5),
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
        },
    },
    productDetailsContainer: {
        marginBottom: theme.spacing(3.25),
        display: 'flex',
        justifyContent: 'space-between',
    },
    productDetailsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    productDetails: {
        display: 'flex',
        fontWeight: 500,
        alignItems: 'baseline',
        color: productDetailsText,
        fontSize: pxToRem(13),
        lineHeight: pxToRem(18),
        letterSpacing: '0.5px',
    },
    productDetailsLabel: {
        lineHeight: 'inherit',
        fontSize: 'inherit',
        fontWeight: 700,
        letterSpacing: '0.5px',
        marginRight: '1rem',
    },
    modalHeader: {
        fontWeight: 400,
        fontSize: pxToRem(24),
        lineHeight: pxToRem(28),
        color: titleColor,
    },
    bpmInfoText: {
        fontWeight: 500,
        fontSize: pxToRem(14),
        color: theme.palette.common.black,
        margin: theme.spacing(0, 2.5, 1, 1),
    },
}))

interface PPlusDialogProps {
    isOpen: boolean
    onRequestClose: () => void
}

const PremiumPlusDialog: FC<PPlusDialogProps> = ({
    isOpen,
    onRequestClose,
}) => {
    const classes = useStyles()

    const {
        product,
        properties: productProperties,
        setProductDetailFormProperties,
        selectedData,
        isManufacturerOptionsReadOnly,
    } = useContext<Partial<ProductContextType>>(ProductContext)

    const {
        msProperties: { properties, selectedOptions, pPlusItems },
        nonMSProperties: { properties: nonMSProps },
        dispatch,
    } = usePPLContext()

    const { productName, sectionName, productType } = product as Partial<
        ListingDetail
    >

    const [activeTab, setActiveTab] = useState('')

    // copy the context of the product listing to make it locally available in the premiumplus dialog
    const initialPPlusItems = useMemo(
        () =>
            Object.entries(productProperties as Record<string, PropertyType>)
                .filter(([_, property]) => isMasterSpecPropertyGroup(property))
                .reduce(
                    (acc, [propertyName, property]) => ({
                        ...acc,
                        [propertyName]: property.reduce(
                            (acc, subProperty) => ({
                                ...acc,
                                [subProperty.subPropertyName as string]: {
                                    ...subProperty.optionGroups?.reduce(
                                        (pPlusGrp, optGrp, index) => {
                                            pPlusGrp[
                                                optGrp.groupId ?? index
                                            ] = optGrp.options?.filter(
                                                (option) =>
                                                    !!option.isPremiumPlus
                                            )
                                            return pPlusGrp
                                        },
                                        {} as any
                                    ),
                                },
                            }),
                            {}
                        ),
                    }),
                    {}
                ),
        [productProperties]
    )

    const initialNonMSProperties = useMemo(
        () =>
            Object.entries(
                productProperties as Record<string, PropertyType>
            ).reduce(
                (acc: Array<NonMSPropertyType>, [propertyName, property]) => {
                    const subProps = property.reduce(
                        (
                            subProperty: Array<NonMSSubPropertyType>,
                            {
                                subPropertyName,
                                propertyId,
                                optionGroups,
                                isPremiumPlus,
                            }: Partial<ManufacturerProductPropertyDetail>
                        ) => {
                            if (
                                subPropertyName &&
                                propertyId &&
                                optionGroups &&
                                !!isPremiumPlus
                            ) {
                                subProperty.push({
                                    subPropertyName,
                                    propertyId,
                                    optionGroups,
                                    isPremiumPlus,
                                    supportedOptionIds:
                                        ((selectedData as SupportedOptionsObject)[
                                            propertyName
                                        ] &&
                                            (selectedData as SupportedOptionsObject)[
                                                propertyName
                                            ][subPropertyName]) ??
                                        [],
                                })
                            }
                            return subProperty
                        },
                        []
                    )

                    if (subProps.length) {
                        acc.push({
                            propertyName,
                            dummyPropertyId: uuidv4(),
                            subProperties: subProps,
                        })
                    }
                    return acc
                },
                []
            ),
        [productProperties, selectedData]
    )

    const arePropertiesDirty = useMemo(
        () =>
            !isEqual(initialPPlusItems, pPlusItems) ||
            !isEqual(selectedData, selectedOptions),
        [initialPPlusItems, pPlusItems, selectedData, selectedOptions, isEqual]
    )

    const isNonMSDirty = useMemo(
        () => !isEqual(initialNonMSProperties, nonMSProps),
        [initialNonMSProperties, nonMSProps]
    )

    const handleTabClick = useCallback((e) => {
        setActiveTab(e.target.innerText.split(' ')[0])
    }, [])

    const handleCancelClick = useCallback(() => {
        if (!!arePropertiesDirty || isNonMSDirty) {
            return NavPromptDialogContainer(
                JSON.stringify({
                    title: 'Manage Premium Plus Properties',
                    preferredMessage: UnsavedChanges,
                    buttonColor: buttonSecondary,
                    confirmText: 'Yes',
                    cancelText: 'No',
                } as NavPromptMessageProps),
                (callbackState) => {
                    if (callbackState) {
                        onRequestClose()
                    }
                }
            )
        }

        onRequestClose()
    }, [arePropertiesDirty, onRequestClose, isNonMSDirty])

    const handleApply = useCallback(() => {
        if (setProductDetailFormProperties) {
            setProductDetailFormProperties(
                composePropertiesInput(properties, pPlusItems, nonMSProps),
                composeSelectedOptions(selectedOptions, nonMSProps)
            )
            onRequestClose()
        }
    }, [
        properties,
        pPlusItems,
        nonMSProps,
        selectedOptions,
        onRequestClose,
        setProductDetailFormProperties,
    ])

    useEffect(() => {
        // need to set this here to reflect MUI active tab style
        setActiveTab('MasterSpec')
        dispatch({
            type: PPL_ACTIONS.batchMSActions,
            payload: [
                {
                    type: PPL_ACTIONS.setPPlusItems,
                    payload: initialPPlusItems,
                },
                {
                    type: PPL_ACTIONS.setSelectedOptions,
                    payload: selectedData,
                },
                {
                    type: PPL_ACTIONS.setDefaultProperties,
                    payload: productProperties,
                },
            ],
        })
        dispatch({
            type: PPL_ACTIONS.setPristineNonMS,
            payload: initialNonMSProperties,
        })

        // NOTE: this is used as componentDidMount and need not a dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Dialog
            open={isOpen}
            onClose={onRequestClose}
            maxWidth="md"
            disableBackdropClick={true}
            disableEscapeKeyDown={true}
            className={classes.dialogContainer}
        >
            <DialogTitle className={classes.title}>
                <Typography
                    variant="h6"
                    component="span"
                    className={classes.modalHeader}
                >
                    {MANUFACTURER_OPTIONS}
                </Typography>
                <IconButton
                    className={classes.closeButton}
                    data-testid="close-btn"
                    aria-label="close"
                    size="small"
                    onClick={handleCancelClick}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Box className={classes.productDetailsContainer}>
                    <Typography
                        component="div"
                        className={classes.productDetailsWrapper}
                    >
                        <Typography
                            className={classes.productDetails}
                            component="div"
                        >
                            <Typography
                                className={classes.productDetailsLabel}
                                component="span"
                            >
                                Product:
                            </Typography>
                            {productName}
                        </Typography>
                        <Typography
                            className={classes.productDetails}
                            component="div"
                        >
                            <Typography
                                className={classes.productDetailsLabel}
                                component="span"
                            >
                                Family:
                            </Typography>
                            {sectionName}
                        </Typography>
                        <Typography
                            className={classes.productDetails}
                            component="div"
                        >
                            <Typography
                                className={classes.productDetailsLabel}
                                component="span"
                            >
                                Product Type:
                            </Typography>
                            {productType}
                        </Typography>
                    </Typography>
                    <Typography
                        component="div"
                        className={classes.productDetailsWrapper}
                    >
                        <PremiumInfoIconTooltip isDialog />
                    </Typography>
                </Box>
                {!!isManufacturerOptionsReadOnly && (
                    <Typography
                        component="div"
                        className={classes.bpmInfoText}
                        data-testid="infoText"
                    >
                        {MANUFACTURER_OPTION_INFO_DIALOG_TEXT}
                    </Typography>
                )}

                <Tabs value={activeTab} aria-label="Product Properties">
                    <Tab
                        data-testid="moDialogMasterSpecTab"
                        label={MASTERSPEC_OPTIONS}
                        onClick={handleTabClick}
                        className={classes.tabs}
                        disableRipple
                        value="MasterSpec"
                    />
                    <Tab
                        data-testid="moDialogManufacturerTab"
                        label={MANUFACTURER_OPTIONS}
                        onClick={handleTabClick}
                        className={classes.tabs}
                        disableRipple
                        value="Manufacturer"
                    />
                </Tabs>
                <GridContainer className={classes.propertiesContainer} item>
                    {activeTab === 'MasterSpec' ? (
                        <PropertiesContainer
                            properties={properties}
                            selectedData={selectedOptions}
                            mode="premPlus"
                        />
                    ) : !!isManufacturerOptionsReadOnly ? (
                        <NonMSContainerViewMode />
                    ) : (
                        <NonMSContainer />
                    )}
                </GridContainer>
            </DialogContent>
            <DialogActions className={classes.actionContainer}>
                <RoundButton
                    hasShadow={false}
                    borderColor={buttonSecondary}
                    color={buttonSecondary}
                    backgroundColor={defaultLight}
                    data-testid="moDialogCancelButton"
                    onClick={handleCancelClick}
                >
                    Cancel
                </RoundButton>
                {!isManufacturerOptionsReadOnly && (
                    <RoundButton
                        hasShadow={false}
                        borderColor={buttonSecondary}
                        color={defaultLight}
                        backgroundColor={buttonSecondary}
                        data-testid="moDialogApplyButton"
                        onClick={handleApply}
                        disabled={!arePropertiesDirty && !isNonMSDirty}
                    >
                        Apply
                    </RoundButton>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default PremiumPlusDialog
